// Profile.js
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axiosInstance from '../utils/axiosInstance';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';

import { 
  User, Package, CreditCard, DollarSign, 
  Save, Edit2, X, CheckCircle, AlertCircle, Loader,
  FileText, Mail, Phone, BarChart, Users, MessageCircle,
  RefreshCw, ArrowUpCircle, ChartBar, MapPin, XCircle
} from 'lucide-react';
import { CreditCard as CreditCardIcon } from 'lucide-react';

const tabs = [
  { name: 'Profile Settings', key: 'settings', icon: User },
  { name: 'Membership', key: 'membership', icon: CreditCard },
  { name: 'Payments', key: 'payments', icon: DollarSign },
  { name: 'Usage Statistics', key: 'usage', icon: BarChart }
];

const ProfileSection = ({ title, children, onEdit, onSave, onCancel, isEditing, description }) => (
  <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
    <div className="flex justify-between items-center mb-2">
      <h3 className="text-xl font-bold text-white">{title}</h3>
      <div>
        {isEditing ? (
          <div className="flex gap-2">
            <button
              onClick={onSave}
              className="flex items-center gap-2 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <Save size={16} />
              Save
            </button>
            <button
              onClick={onCancel}
              className="flex items-center gap-2 px-3 py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              <X size={16} />
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={onEdit}
            className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Edit2 size={16} />
            Edit
          </button>
        )}
      </div>
    </div>
    {description && (
      <p className="text-gray-400 text-sm mb-4">{description}</p>
    )}
    {children}
  </div>
);

const InputField = ({ label, name, value, onChange, disabled, type = "text", placeholder, description, readOnly = false }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-300 mb-1" htmlFor={name}>
      {label}
    </label>
    {description && (
      <p className="text-xs text-gray-400 mb-2">{description}</p>
    )}
    <input
      type={type}
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      readOnly={readOnly}
      placeholder={placeholder}
      className={`w-full px-4 py-2 rounded-md ${
        readOnly 
          ? 'bg-gray-900 border-gray-700 text-gray-500 cursor-not-allowed'
          : disabled 
            ? 'border-gray-600 bg-gray-800 text-gray-500' 
            : 'border-gray-600 text-white bg-gray-700'
      } border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`}
    />
  </div>
);

// Mapping of your tiers to Stripe price IDs
const PRICE_ID_BY_TIER = {
  basic: 'price_1RE0asE1a6rnB8cNkqBkhHi9',
  pro:   'price_1RE0asE1a6rnB8cNhmT34chW',
  enterprise: 'price_1RE0asE1a6rnB8cNpTsMYNaV'
};

// Tier order for comparing plan levels
const TIER_ORDER = ['basic','pro','enterprise'];

// Base URL for Cloud Run direct upgrade endpoint
const CLOUDRUN_URL = process.env.REACT_APP_CLOUDRUN_URL || 'https://direct-upgrade-217971178414.us-central1.run.app';

// Debug environment variables
console.log('DEBUG: Environment Variables:');
console.log('DEBUG: CLOUDRUN_URL =', CLOUDRUN_URL);
console.log('DEBUG: REACT_APP_CLOUDRUN_URL =', process.env.REACT_APP_CLOUDRUN_URL);
console.log('DEBUG: REACT_APP_STRIPE_PUBLISHABLE_KEY available:', !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Tier debugging
console.log('DEBUG: PRICE_ID_BY_TIER =', PRICE_ID_BY_TIER);
console.log('DEBUG: TIER_ORDER =', TIER_ORDER);

// Initialize Stripe.js
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Update plan information to match pricing page
const plans = [
  {
    name: 'Basic',
    price: '$49.00/month',
    features: [
      'Up to 25 customers',
      '150 SMS/Email notifications per month',
      'Customer Management',
      'Job Scheduling',
      '10 Route Optimizations per month',
      'Basic Features'
    ],
    limits: {
      customers: 25,
      sms: 150,
      email: 150,
      route: 10
    },
    tier: 'basic'
  },
  {
    name: 'Pro',
    price: '$79.00/month',
    features: [
      'Up to 100 customers',
      '1000 SMS/Email notifications per month',
      'All Basic features',
      'Priority Support',
      '50 Route Optimizations per month',
      'Advanced Analytics'
    ],
    limits: {
      customers: 100,
      sms: 1000,
      email: 1000,
      route: 50
    },
    tier: 'pro'
  },
  {
    name: 'Enterprise',
    price: '$199.00/month',
    features: [
      'Unlimited customers',
      'Unlimited SMS/Email notifications',
      'All Pro features',
      '100 Route Optimizations per month',
      'Custom Solutions',
      'Dedicated Support',
      'Priority Email & Phone Support'
    ],
    limits: {
      customers: Infinity,
      sms: Infinity,
      email: Infinity,
      route: 100
    },
    tier: 'enterprise'
  }
];

// Update the confirmation modal component to handle auto-archive warnings
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText, cancelText, isUpgrade, warningInfo }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-white">{title}</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>
        
        <div className="mb-6">
          <div className={`p-4 rounded-lg mb-4 flex items-start ${isUpgrade ? 'bg-blue-900/30 border border-blue-700' : 'bg-yellow-900/30 border border-yellow-700'}`}>
            <div className="mt-1 mr-4">
              {isUpgrade ? 
                <ArrowUpCircle className="h-6 w-6 text-blue-400" /> : 
                <AlertCircle className="h-6 w-6 text-yellow-400" />
              }
            </div>
            <p className="text-gray-300 whitespace-pre-line">{message}</p>
          </div>
          
          {/* Add auto-archive warning if provided */}
          {warningInfo && (
            <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg mb-4 flex items-start">
              <AlertCircle className="h-6 w-6 text-red-400 mr-4 mt-1 flex-shrink-0" />
              <div>
                <p className="text-gray-300 font-medium mb-2">Warning: Customer Auto-Archive</p>
                <p className="text-gray-300 text-sm">
                  {warningInfo.message}
                </p>
                {warningInfo.count > 0 && (
                  <div className="mt-3 bg-gray-700/50 p-3 rounded text-sm">
                    <p className="font-medium text-yellow-300 mb-1">Auto-Archive Details:</p>
                    <ul className="list-disc list-inside text-gray-300 space-y-1">
                      <li>{warningInfo.count} customers will be automatically archived</li>
                      <li>Status will change from "Active" to "Inactive"</li>
                      <li>This will occur at the end of your current billing period</li>
                      <li>Your customer data will be preserved and can be reactivated later</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="flex space-x-4">
          <button
            onClick={onConfirm}
            className={`flex-1 px-4 py-2 rounded-md ${isUpgrade ? 'bg-blue-600 hover:bg-blue-700' : 'bg-yellow-600 hover:bg-yellow-700'} text-white`}
          >
            {confirmText || 'Confirm'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md"
          >
            {cancelText || 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Success Message Modal Component
const SuccessModal = ({ isOpen, onClose, message, onContinue }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-green-700 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-white">Success!</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>
        
        <div className="mb-6">
          <div className="p-4 bg-green-900/30 border border-green-700 rounded-lg mb-4 flex items-start">
            <CheckCircle className="h-6 w-6 text-green-400 mr-4 mt-1 flex-shrink-0" />
            <p className="text-gray-300">{message}</p>
          </div>
        </div>
        
        <button
          onClick={onContinue || onClose}
          className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

const Profile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState('settings');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const { user, refreshUser } = useAuth();
  const [formData, setFormData] = useState({
    // Account info (read-only)
    accountName: '',
    accountEmail: '',
    
    // Profile info (editable)
    displayName: '',
    contactEmail: '',
    contactPhone: '',
    customerBaseSize: '',
    
    // Business info
    businessName: '',
    businessWebsite: '',
    businessAddress: ''
  });
  const [originalData, setOriginalData] = useState(null);
  const [stripeStatus, setStripeStatus] = useState({
    isConnected: false,
    accountId: null,
    isOnboarded: false
  });
  
  // Usage statistics state
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState('');

  // Add new state for modals
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState({
    title: '',
    message: '',
    confirmText: '',
    cancelText: '',
    onConfirm: () => {},
    isUpgrade: true
  });
  
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successDetails, setSuccessDetails] = useState({
    message: '',
    onContinue: () => {}
  });

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/profile');
      console.log('Fetched profile data:', response.data);
      
      // Extract profile or user data depending on API response structure
      const userData = response.data.user || response.data;
      const profileData = userData.profile || {};
      
      // Log to help debug business info
      console.log('Business info from API:', profileData.businessInfo);
      
      const formattedData = {
        // Account info (read-only)
        accountName: userData.name || '',
        accountEmail: userData.email || '',
        
        // Profile info (editable)
        displayName: profileData.displayName || '',
        contactEmail: profileData.contactEmail || '',
        contactPhone: profileData.contactPhone || '',
        customerBaseSize: profileData.customerBaseSize || '',
        
        // Business info - ensure we get it from the businessInfo object
        businessName: profileData.businessInfo?.name || '',
        businessWebsite: profileData.businessInfo?.website || '',
        businessAddress: profileData.businessInfo?.address || ''
      };
      
      console.log('Formatted profile data for form:', formattedData);
      setFormData(formattedData);
      setOriginalData(formattedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to fetch profile data.');
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if there's a tab parameter in the URL
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && tabs.some(tab => tab.key === tabParam)) {
      setCurrentTab(tabParam);
    }
    
    // Handle Stripe checkout session completion
    const sessionId = params.get('session_id');
    if (sessionId) {
      const verifyCheckoutSession = async () => {
        try {
          const response = await axiosInstance.post('/payment/verify-session', { sessionId });
          if (response.data.success) {
            // Clear the session_id parameter from URL without reloading
            const newParams = new URLSearchParams(params);
            newParams.delete('session_id');
            navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
            
            // Refresh user data and show success message
            await refreshUser();
            setSuccess(response.data.message || 'Subscription updated successfully!');
          }
        } catch (error) {
          console.error('Error verifying checkout session:', error);
          setError('There was an issue confirming your subscription. Please contact support if you were charged.');
        }
      };
      
      verifyCheckoutSession();
    }
    
    if (user) {
      fetchProfile();
      
      // Check if we need to refresh Stripe status based on cache
      const cachedStripeStatus = getStripeStatusFromCache();
      if (cachedStripeStatus) {
        console.log('Profile: Using cached Stripe status');
        setStripeStatus(cachedStripeStatus);
      } else {
        console.log('Profile: No cached Stripe status, fetching fresh data');
        checkStripeStatus();
      }
      
      // If the current tab is 'usage', fetch usage statistics
      console.log(`Profile useEffect: Checking if tab '${currentTab}' or param '${tabParam}' is 'usage'`);
      if (currentTab === 'usage' || tabParam === 'usage') {
        console.log('Profile useEffect: Triggering fetchUsageStats');
        fetchUsageStats();
      }
      
      setLoading(false);
    }
  }, [user, location.search, currentTab, navigate, refreshUser]);

  // Add a new useEffect to refresh Stripe status when tab changes to 'payments'
  useEffect(() => {
    if (currentTab === 'payments') {
      // Force refresh Stripe status when switching to payments tab
      checkStripeStatus(true);
    }
  }, [currentTab]);

  // Function to get cached Stripe status
  const getStripeStatusFromCache = () => {
    try {
      const cachedData = localStorage.getItem('stripeStatus');
      if (!cachedData) return null;
      
      const { status, timestamp } = JSON.parse(cachedData);
      const cacheAge = Date.now() - timestamp;
      
      // Cache expires after 30 minutes (1800000 ms)
      if (cacheAge < 1800000) {
        return status;
      } else {
        console.log('Profile: Stripe status cache expired');
        return null;
      }
    } catch (error) {
      console.error('Profile: Error reading Stripe status from cache:', error);
      return null;
    }
  };

  // Function to cache Stripe status
  const cacheStripeStatus = (status) => {
    try {
      const cacheData = {
        status,
        timestamp: Date.now()
      };
      localStorage.setItem('stripeStatus', JSON.stringify(cacheData));
    } catch (error) {
      console.error('Profile: Error caching Stripe status:', error);
    }
  };
  
  // Add this function to fetch usage statistics
  const fetchUsageStats = async () => {
    try {
      setUsageLoading(true);
      setUsageError('');
      const response = await axiosInstance.get('/usage');
      console.log('Profile.js - Usage data received:', response.data);
      setUsageData(response.data);
    } catch (error) {
      console.error('Error fetching usage statistics:', error);
      setUsageError('Failed to fetch usage statistics. Please try again.');
    } finally {
      setUsageLoading(false);
    }
  };
  
  // Format date for usage statistics
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const checkStripeStatus = async (forceRefresh = false) => {
    try {
      // If not forcing refresh, check cache first
      if (!forceRefresh) {
        const cachedStatus = getStripeStatusFromCache();
        if (cachedStatus) {
          console.log('Profile: Using cached Stripe status');
          setStripeStatus(cachedStatus);
          return;
        }
      }
      
      console.log('Profile: Checking Stripe status');
      const response = await axiosInstance.get('/connect/status');
      console.log('Profile: Stripe status response:', response.data);
      
      const statusData = {
        isConnected: !!response.data.accountId,
        accountId: response.data.accountId,
        isOnboarded: response.data.isOnboarded
      };
      
      setStripeStatus(statusData);
      
      // Cache the status
      cacheStripeStatus(statusData);
    } catch (error) {
      console.error('Profile: Error checking Stripe status:', error);
      setStripeStatus({
        isConnected: false,
        accountId: null,
        isOnboarded: false
      });
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEdit = () => {
    setOriginalData(formData);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFormData(originalData);
    setIsEditing(false);
    setError('');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      setLoading(true);
      
      // Log the data we're sending to the server
      console.log('Submitting profile data:', {
        displayName: formData.displayName,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        customerBaseSize: formData.customerBaseSize,
        businessInfo: {
          name: formData.businessName,
          website: formData.businessWebsite,
          address: formData.businessAddress
        }
      });
      
      const response = await axiosInstance.put('/profile', {
        displayName: formData.displayName,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        customerBaseSize: formData.customerBaseSize,
        businessInfo: {
          name: formData.businessName,
          website: formData.businessWebsite,
          address: formData.businessAddress
        }
      });
      
      console.log('Profile update response:', response.data);
      
      // Extract the profile data from the response
      const responseData = response.data.profile || response.data;
      
      // We'll use the form data we sent since the API might return different structure
      // But update it based on any changes the server made
      const updatedData = {
        ...formData,
        displayName: responseData.displayName || formData.displayName,
        contactEmail: responseData.contactEmail || formData.contactEmail,
        contactPhone: responseData.contactPhone || formData.contactPhone,
        customerBaseSize: responseData.customerBaseSize || formData.customerBaseSize,
        
        // Business info - make sure we get the right values
        businessName: responseData.businessInfo?.name || formData.businessName,
        businessWebsite: responseData.businessInfo?.website || formData.businessWebsite,
        businessAddress: responseData.businessInfo?.address || formData.businessAddress
      };
      
      console.log('Updated form data after save:', updatedData);
      
      setFormData(updatedData);
      setOriginalData(updatedData);
      setIsEditing(false);
      setSuccess('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeSubscription = async (tier) => {
    try {
      console.log('=== UPGRADE DEBUG ===');
      console.log(`Starting upgrade process for tier: ${tier}`);
      console.log(`Current user subscription tier: ${user.subscriptionTier}`);
      console.log(`User ID: ${user._id}`);
      
      // Determine if this is an upgrade, downgrade, or same tier
      const currentTierIndex = TIER_ORDER.indexOf(user.subscriptionTier);
      const newTierIndex = TIER_ORDER.indexOf(tier);
      const isUpgrade = newTierIndex > currentTierIndex;
      const isDowngrade = newTierIndex < currentTierIndex;
      const isSameTier = newTierIndex === currentTierIndex;
      
      // Get price information
      const currentPrice = user.subscriptionTier === 'basic' ? 49 : 
                          user.subscriptionTier === 'pro' ? 79 : 
                          user.subscriptionTier === 'enterprise' ? 199 : 0;
      
      const newPrice = tier === 'basic' ? 49 : 
                      tier === 'pro' ? 79 : 
                      tier === 'enterprise' ? 199 : 0;
      
      const priceDiff = newPrice - currentPrice;
      
      // Get current customer count
      let customerCount = 0;
      let warningInfo = null;
      
      // If downgrading, check if we'll exceed new plan's limit
      if (isDowngrade) {
        try {
          const countResponse = await axiosInstance.get('/customers/count');
          customerCount = countResponse.data.count || 0;
          
          console.log(`Current customer count: ${customerCount}`);
          
          // Determine new plan's customer limit
          const newPlanLimit = tier === 'basic' ? 25 : 
                              tier === 'pro' ? 100 : 
                              Infinity;
          
          // If exceeding limit, prepare warning info
          if (customerCount > newPlanLimit) {
            const excessCount = customerCount - newPlanLimit;
            warningInfo = {
              count: excessCount,
              message: `Your account has ${customerCount} customers, but the ${tier.charAt(0).toUpperCase() + tier.slice(1)} plan only allows ${newPlanLimit}.`
            };
            
            console.log(`Warning: ${excessCount} customers will be auto-archived`);
          }
        } catch (error) {
          console.error('Error fetching customer count:', error);
        }
      }
      
      // Create confirmation message based on change type
      let confirmationTitle = '';
      let confirmationMessage = '';
      let confirmText = '';
      let cancelText = 'Cancel';
      
      if (user.subscriptionTier === tier && user.subscriptionCancelledAt) {
        confirmationTitle = 'Resume Subscription';
        confirmationMessage = `Are you sure you want to resume your ${tier.charAt(0).toUpperCase() + tier.slice(1)} plan subscription?`;
        confirmText = 'Resume';
      } else if (isUpgrade) {
        confirmationTitle = 'Confirm Upgrade';
        confirmationMessage = `Are you sure you want to upgrade from ${user.subscriptionTier.charAt(0).toUpperCase() + user.subscriptionTier.slice(1)} to ${tier.charAt(0).toUpperCase() + tier.slice(1)}?\n\n` +
          `Your monthly subscription will increase by $${priceDiff.toFixed(2)}.\n\n` +
          `You'll be charged a prorated amount for the remainder of your current billing cycle.`;
        confirmText = 'Upgrade';
      } else if (isDowngrade) {
        confirmationTitle = 'Confirm Downgrade';
        confirmationMessage = `Are you sure you want to downgrade from ${user.subscriptionTier.charAt(0).toUpperCase() + user.subscriptionTier.slice(1)} to ${tier.charAt(0).toUpperCase() + tier.slice(1)}?\n\n` +
          `Your monthly subscription will decrease by $${Math.abs(priceDiff).toFixed(2)}.\n\n` + 
          `The change will take effect at the end of your current billing cycle.`;
        confirmText = 'Downgrade';
      } else {
        // Show an in-app message for same tier
        setSuccess(`You're already subscribed to the ${tier.charAt(0).toUpperCase() + tier.slice(1)} plan.`);
        return;
      }
      
      // If same tier and not cancelled, no need to proceed
      if (isSameTier && !user.subscriptionCancelledAt) {
        setSuccess(`You're already subscribed to the ${tier.charAt(0).toUpperCase() + tier.slice(1)} plan.`);
        return;
      }
      
      // Set up confirmation details and show modal
      setConfirmDetails({
        title: confirmationTitle,
        message: confirmationMessage,
        confirmText,
        cancelText,
        isUpgrade,
        warningInfo,
        onConfirm: async () => {
          // Close the confirmation modal
          setShowConfirmModal(false);
          
          // Proceed with the actual subscription change
          await processSubscriptionChange(tier, warningInfo);
        }
      });
      
      // Show the confirmation modal
      setShowConfirmModal(true);
      
    } catch (error) {
      console.error('Error preparing subscription change:', error);
      setError('An unexpected error occurred. Please try again.');
    }
  };
  
  // Update processSubscriptionChange to handle auto-archive information
  const processSubscriptionChange = async (tier, warningInfo) => {
    try {
      setLoading(true);
      setError('');

      // Check if this is resuming a cancelled subscription
      if (user.subscriptionTier === tier && user.subscriptionCancelledAt) {
        console.log('DEBUG: Resuming cancelled subscription');
        // Simply resume the existing subscription
        const resumeResponse = await axiosInstance.post('/subscription/resume');
        console.log('DEBUG: Resume response:', resumeResponse.data);
        await refreshUser();
        
        let nextBillingDate;
        try {
          nextBillingDate = formatDate(resumeResponse.data.nextBillingDate || new Date());
        } catch (dateError) {
          nextBillingDate = "your next billing date";
        }
        
        // Show success modal
        setSuccessDetails({
          message: `Your subscription has been resumed successfully. Your next billing date will be ${nextBillingDate}.`,
          onContinue: () => {
            // Close the modal and reload the page
            setShowSuccessModal(false);
            window.location.reload();
          }
        });
        setShowSuccessModal(true);
        
        setLoading(false);
        return;
      }

      // Handle upgrade, downgrade, or new subscription
      const currentTier = user.subscriptionTier;
      console.log(`DEBUG: Current tier: ${currentTier}, Target tier: ${tier}`);
      console.log(`DEBUG: TIER_ORDER indices - Current: ${TIER_ORDER.indexOf(currentTier)}, Target: ${TIER_ORDER.indexOf(tier)}`);
      
      if (currentTier && TIER_ORDER.indexOf(tier) > TIER_ORDER.indexOf(currentTier)) {
        // Add detailed logging to troubleshoot the 400 Bad Request
        console.log('DEBUG: Attempting direct upgrade with:', { 
          userId: user._id, 
          idType: typeof user._id, 
          newPriceId: PRICE_ID_BY_TIER[tier],
          cloudRunUrl: CLOUDRUN_URL
        });
        
        // Direct in-place upgrade with proration via Cloud Run
        try {
          console.log(`DEBUG: Making POST request to ${CLOUDRUN_URL}/upgrade with data:`, {
            userId: user._id,
            newPriceId: PRICE_ID_BY_TIER[tier]
          });
          
          const resp = await axios.post(
            `${CLOUDRUN_URL}/upgrade`,
            { userId: user._id, newPriceId: PRICE_ID_BY_TIER[tier] }
          );
          
          console.log('DEBUG: Upgrade response success:', resp.data);
          console.log('DEBUG: Response headers:', resp.headers);
          await refreshUser();
          
          // Add UI notification for successful upgrade
          // Fix date handling to avoid Invalid time value error
          let nextBillingDate;
          try {
            // Try to get the date from response or user object
            if (resp.data.nextBillingDate) {
              nextBillingDate = formatDate(resp.data.nextBillingDate);
            } else if (user.currentPeriodEnd) {
              nextBillingDate = formatDate(user.currentPeriodEnd);
            } else {
              // Fallback to "your next billing date" if we can't determine it
              nextBillingDate = "your next billing date";
            }
          } catch (dateError) {
            console.error('Error formatting date:', dateError);
            nextBillingDate = "your next billing date";
          }
          
          // Show success modal
          setSuccessDetails({
            message: `Subscription successfully upgraded to ${tier.charAt(0).toUpperCase() + tier.slice(1)}! Your plan has been updated and will renew on ${nextBillingDate}.`,
            onContinue: () => {
              // Close the modal and reload the page
              setShowSuccessModal(false);
              window.location.reload();
            }
          });
          setShowSuccessModal(true);
          
        } catch (upgradeError) {
          console.error('DEBUG: Upgrade API error:', upgradeError);
          console.error('DEBUG: Upgrade API error message:', upgradeError.message);
          
          if (upgradeError.response) {
            console.error('DEBUG: Upgrade API error status:', upgradeError.response.status);
            console.error('DEBUG: Upgrade API error data:', upgradeError.response.data);
            console.error('DEBUG: Upgrade API error headers:', upgradeError.response.headers);
          }
          
          if (upgradeError.request) {
            console.error('DEBUG: Upgrade API request made but no response received');
          }
          
          throw upgradeError;
        }
        
        setLoading(false);
        return;
      } else if (currentTier && TIER_ORDER.indexOf(tier) < TIER_ORDER.indexOf(currentTier)) {
        console.log('DEBUG: Processing downgrade');
        
        // Schedule downgrade at period end
        let downgradePlanData = {
          newPriceId: PRICE_ID_BY_TIER[tier]
        };
        
        // If we have warning info for auto-archive, include it in the request
        if (warningInfo && warningInfo.count > 0) {
          downgradePlanData.autoArchive = {
            enabled: true,
            customerLimit: tier === 'basic' ? 25 : tier === 'pro' ? 100 : Infinity,
            excessCount: warningInfo.count
          };
        }
        
        const resp = await axiosInstance.post('/subscription/update', downgradePlanData);
        console.log('DEBUG: Downgrade response:', resp.data);
        await refreshUser();
        
        let nextBillingDate;
        try {
          nextBillingDate = formatDate(resp.data.nextBillingDate || new Date());
        } catch (dateError) {
          nextBillingDate = "the end of your current billing period";
        }
        
        // Customize message based on whether auto-archive will happen
        let successMessage = `Downgrade scheduled. Your plan will switch to ${tier.charAt(0).toUpperCase() + tier.slice(1)} on ${nextBillingDate}.`;
        
        if (warningInfo && warningInfo.count > 0) {
          successMessage += ` ${warningInfo.count} customers will be automatically archived at that time.`;
        }
        
        // Show success modal
        setSuccessDetails({
          message: successMessage,
          onContinue: () => {
            // Close the modal and reload the page
            setShowSuccessModal(false);
            window.location.reload();
          }
        });
        setShowSuccessModal(true);
        
        setLoading(false);
        return;
      } else {
        console.log('DEBUG: Processing new subscription');
        // New subscription or first-time trial
        const priceId = PRICE_ID_BY_TIER[tier];
        console.log('DEBUG: Creating checkout session with priceId:', priceId);
        const response = await axiosInstance.post(
          '/payment/create-checkout-session',
          { priceId }
        );
        console.log('DEBUG: Checkout session response:', response.data);
        const stripe = await stripePromise;
        const { sessionId: newSessionId } = response.data;
        console.log('DEBUG: Redirecting to Stripe checkout with sessionId:', newSessionId);
        const result = await stripe.redirectToCheckout({ sessionId: newSessionId });
        if (result.error) throw result.error;
      }
      
    } catch (error) {
      console.error('Error updating subscription:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
      }
      
      let errorMessage = 'Failed to update subscription. Please try again or contact support.';
      
      // Provide more specific error messages based on response
      if (error.response?.status === 401) {
        errorMessage = 'Authentication error. Please log in and try again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to perform this action.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Subscription service not found. Please contact support.';
      } else if (error.response?.status === 400) {
        errorMessage = `Bad request: ${error.response.data.message || 'Unknown error'}`;
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message.includes('Network Error')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Attempting to resume subscription');
      const resumeResponse = await axiosInstance.post('/subscription/resume');
      console.log('Resume response:', resumeResponse.data);
      
      await refreshUser();
      setSuccess('Your subscription has been resumed successfully. Your next billing date will be ' +
                formatDate(resumeResponse.data.subscription?.current_period_end * 1000 || new Date()));
    } catch (error) {
      console.error('Error resuming subscription:', error);
      setError('Failed to resume subscription. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = () => {
    setConfirmDetails({
      title: 'Cancel Subscription',
      message: 'Are you sure you want to cancel your subscription?\n\n' +
        '• Your subscription will remain active until the end of your current billing period.\n' +
        '• You will have full access to all features until then.\n' +
        '• Your data will be preserved if you decide to resubscribe in the future.\n' +
        '• No further charges will be made after cancellation.',
      confirmText: 'Cancel Subscription',
      cancelText: 'Keep Subscription',
      isUpgrade: false,
      onConfirm: async () => {
        setShowConfirmModal(false);
        setLoading(true);
        
        try {
          const response = await axiosInstance.post('/subscription/cancel');
          
          // Refresh user data to get updated subscription status
          await refreshUser();
          
          // Show success modal
          setSuccessDetails({
            message: 'Your subscription has been cancelled successfully. ' +
              `You will continue to have access until ${formatDate(response.data.endDate || new Date())}. ` +
              'Your data will be preserved if you decide to return in the future.',
            onContinue: () => {
              setShowSuccessModal(false);
              window.location.reload();
            }
          });
          setShowSuccessModal(true);
        } catch (error) {
          console.error('Error cancelling subscription:', error);
          setError('Failed to cancel subscription. Please try again or contact support.');
        } finally {
          setLoading(false);
        }
      }
    });
    
    setShowConfirmModal(true);
  };

  const handleConnectStripe = async () => {
    try {
      setError('');
      setSuccess('');
      console.log('Profile: Initializing Stripe Connect...');
      
      // Clear the cache before connecting
      localStorage.removeItem('stripeStatus');
      
      const response = await axiosInstance.post('/connect/init');
      
      if (response.data.status === 'already_onboarded') {
        console.log('Profile: Stripe already connected:', response.data.message);
        setSuccess(response.data.message);
        checkStripeStatus(true); // Force refresh the status
        return;
      }
      
      if (response.data.url) {
        console.log('Profile: Redirecting to Stripe Connect onboarding...');
        window.location.href = response.data.url;
      } else {
        setError('Failed to get Stripe Connect URL. Please try again.');
      }
    } catch (error) {
      console.error('Profile: Error connecting to Stripe:', error);
      if (error.response?.data?.error?.includes('platform-profile')) {
        setError('Stripe platform setup is incomplete. Please contact support to complete the setup.');
      } else {
        setError('Failed to initialize Stripe Connect. Please try again.');
      }
    }
  };

  const handleCompleteStripeSetup = async () => {
    try {
      // Clear the cache before completing setup
      localStorage.removeItem('stripeStatus');
      
      const response = await axiosInstance.post('/connect/refresh-link');
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      setError('Failed to get Stripe setup link. Please try again.');
    }
  };

  const handleViewStripeAccount = async () => {
    try {
      const response = await axiosInstance.get('/connect/login-link');
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      setError('Failed to access Stripe dashboard. Please try again.');
    }
  };

  const handleDisconnectStripe = async () => {
    if (window.confirm('Are you sure you want to disconnect your Stripe account? This is for testing purposes only.')) {
      try {
        setError('');
        setSuccess('');
        
        // Clear the cache before disconnecting
        localStorage.removeItem('stripeStatus');
        
        const response = await axiosInstance.post('/connect/disconnect');
        setSuccess('Stripe account disconnected for testing purposes.');
        checkStripeStatus(true); // Force refresh the status
        refreshUser(); // Refresh the user data to update the UI
      } catch (error) {
        setError('Failed to disconnect Stripe account: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleTabChange = (tabKey) => {
    setCurrentTab(tabKey);
    
    // Update URL with tab parameter
    const params = new URLSearchParams(location.search);
    params.set('tab', tabKey);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    
    // If switching to usage tab, fetch usage statistics
    console.log(`Profile handleTabChange: Checking if new tabKey '${tabKey}' is 'usage'`);
    if (tabKey === 'usage') {
      console.log('Profile handleTabChange: Triggering fetchUsageStats');
      fetchUsageStats();
    }
  };

  const renderSubscriptionDetails = () => {
    return (
      <>
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">Current Subscription</h2>
          <div className="bg-gray-700/50 p-5 rounded-lg border border-gray-600">
            <div className="space-y-2 text-gray-300">
              <p><span className="font-medium">Plan:</span> {user.subscriptionTier ? user.subscriptionTier.charAt(0).toUpperCase() + user.subscriptionTier.slice(1) : 'None'}</p>
              
              {/* Enhanced status display */}
              <p>
                <span className="font-medium">Status:</span> 
                <span className={`ml-2 px-2 py-0.5 rounded-full text-sm ${
                  user.subscriptionActive && !user.subscriptionCancelledAt 
                    ? 'bg-green-900/30 text-green-400' 
                    : user.subscriptionActive && user.subscriptionCancelledAt
                      ? 'bg-yellow-900/30 text-yellow-400'
                      : 'bg-red-900/30 text-red-400'
                }`}>
                  {user.subscriptionActive && !user.subscriptionCancelledAt
                    ? 'Active'
                    : user.subscriptionActive && user.subscriptionCancelledAt
                      ? 'Cancelled - Access until period end'
                      : 'Inactive'}
                </span>
              </p>
              
              {/* Show billing period information */}
              {user.subscriptionActive && (
                <>
                  {user.currentPeriodStart && (
                    <p><span className="font-medium">Current period:</span> {formatDate(user.currentPeriodStart)} - {formatDate(user.currentPeriodEnd || new Date())}</p>
                  )}
                  
                  {user.subscriptionCancelledAt && (
                    <div className="mt-3 p-3 bg-yellow-900/20 border border-yellow-800 rounded-md">
                      <p className="text-yellow-300">
                        Your subscription has been cancelled but will remain active until {formatDate(user.currentPeriodEnd || new Date())}.
                        No further charges will be made.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Subscription action buttons */}
            <div className="mt-4 flex gap-3">
              {user.subscriptionActive && !user.subscriptionCancelledAt && (
                <button
                  onClick={handleCancelSubscription}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Cancel Subscription
                </button>
              )}
              
              {user.subscriptionCancelledAt && (
                <button
                  onClick={() => handleUpgradeSubscription(user.subscriptionTier)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  Resume Subscription
                </button>
              )}
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-2xl font-bold text-white mb-6">Available Plans</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div key={plan.tier} className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-2xl font-bold text-blue-400 mb-4">{plan.price}</p>
                <ul className="mb-6 space-y-2">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-300">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleUpgradeSubscription(plan.tier)}
                  disabled={user.subscriptionTier === plan.tier && user.subscriptionActive && !user.subscriptionCancelledAt}
                  className={`w-full px-4 py-2 rounded-md ${
                    user.subscriptionTier === plan.tier && user.subscriptionActive && !user.subscriptionCancelledAt
                      ? 'bg-gray-600 cursor-not-allowed'
                      : user.subscriptionTier === plan.tier && user.subscriptionCancelledAt
                        ? 'bg-green-600 hover:bg-green-700'
                        : TIER_ORDER.indexOf(plan.tier) > TIER_ORDER.indexOf(user.subscriptionTier)
                          ? 'bg-blue-600 hover:bg-blue-700' 
                          : 'bg-yellow-600 hover:bg-yellow-700'
                  } text-white transition-colors`}
                >
                  {user.subscriptionTier === plan.tier && user.subscriptionActive && !user.subscriptionCancelledAt 
                    ? 'Current Plan' 
                    : user.subscriptionTier === plan.tier && user.subscriptionCancelledAt
                      ? 'Resume Subscription'
                      : TIER_ORDER.indexOf(plan.tier) > TIER_ORDER.indexOf(user.subscriptionTier)
                        ? 'Upgrade'
                        : TIER_ORDER.indexOf(plan.tier) < TIER_ORDER.indexOf(user.subscriptionTier)
                          ? 'Downgrade'
                          : 'Select Plan'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-4">
            <Loader className="w-8 h-8 text-blue-500 animate-spin" />
            <p className="text-gray-400">Loading profile data...</p>
          </div>
        </div>
      );
    }

    switch (currentTab) {
      case 'settings':
        return (
          <>
            {(error || success) && (
              <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                error ? 'bg-red-900/50 border-red-500 text-red-200' : 'bg-green-900/50 border-green-500 text-green-200'
              } border`}>
                {error ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                <p>{error || success}</p>
              </div>
            )}

            <form onSubmit={handleFormSubmit}>
              {/* Business Information (Editable) */}
              <ProfileSection 
                title="Business Information" 
                isEditing={isEditing}
                onEdit={handleEdit}
                onSave={handleFormSubmit}
                onCancel={handleCancel}
                description="This information will appear on your quotes and invoices sent to customers."
              >
                <div className="p-3 mb-4 bg-blue-900/20 border border-blue-800 rounded-md">
                  <div className="flex items-start">
                    <FileText className="h-5 w-5 text-blue-400 mr-2 mt-0.5" />
                    <p className="text-sm text-blue-200">
                      The information below will be displayed on quotes and invoices sent to your customers.
                      Make sure it accurately represents your business.
                    </p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <InputField
                    label="Business Display Name"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleFormChange}
                    disabled={!isEditing}
                    placeholder="Enter your business display name"
                    description="Name displayed on quotes and invoices"
                  />
                  <InputField
                    label="Business Contact Email"
                    name="contactEmail"
                    type="email"
                    value={formData.contactEmail}
                    onChange={handleFormChange}
                    disabled={!isEditing}
                    placeholder="Enter your business contact email"
                    description="Email displayed on quotes and invoices"
                  />
                  <InputField
                    label="Business Phone Number"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleFormChange}
                    disabled={!isEditing}
                    placeholder="Enter your business phone number"
                    description="Phone number displayed on quotes and invoices"
                  />
                  <InputField
                    label="Customer Base Size"
                    name="customerBaseSize"
                    type="number"
                    value={formData.customerBaseSize}
                    onChange={handleFormChange}
                    disabled={!isEditing}
                    placeholder="Enter your customer base size"
                    description="Used to determine your subscription tier"
                  />
                  <InputField
                    label="Business Name"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleFormChange}
                    disabled={!isEditing}
                    placeholder="Enter your business name"
                    description="Legal business name for quotes and invoices"
                  />
                  <InputField
                    label="Business Website"
                    name="businessWebsite"
                    value={formData.businessWebsite}
                    onChange={handleFormChange}
                    disabled={!isEditing}
                    placeholder="Enter your business website"
                    description="Website URL displayed on documents"
                  />
                  <div className="md:col-span-2">
                    <InputField
                      label="Business Address"
                      name="businessAddress"
                      value={formData.businessAddress}
                      onChange={handleFormChange}
                      disabled={!isEditing}
                      placeholder="Enter your business address"
                      description="Physical address displayed on documents"
                    />
                  </div>
                </div>
              </ProfileSection>
              
              {/* Document Preview */}
              <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
                <div className="flex items-center mb-4">
                  <FileText className="h-5 w-5 text-blue-400 mr-2" />
                  <h3 className="text-xl font-bold text-white">Document Preview</h3>
                </div>
                <p className="text-gray-400 text-sm mb-4">
                  This is how your business information will appear on quotes and invoices sent to customers.
                </p>
                
                <div className="bg-white p-6 rounded-md shadow-lg">
                  <div className="border-b border-gray-200 pb-4 mb-4">
                    <h4 className="text-xl font-bold text-gray-800">
                      {formData.displayName || "Your Business Name"}
                    </h4>
                    {formData.businessName && (
                      <p className="text-gray-600 text-sm">{formData.businessName}</p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">Contact Information</h5>
                      <div className="space-y-1">
                        {formData.contactEmail && (
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 text-blue-500 mr-2" />
                            <span>{formData.contactEmail}</span>
                          </div>
                        )}
                        {formData.contactPhone && (
                          <div className="flex items-center">
                            <Phone className="h-4 w-4 text-blue-500 mr-2" />
                            <span>{formData.contactPhone}</span>
                          </div>
                        )}
                        {formData.businessWebsite && (
                          <div className="flex items-center">
                            <svg className="h-4 w-4 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                            </svg>
                            <span>{formData.businessWebsite}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      {formData.businessAddress && (
                        <>
                          <h5 className="font-semibold text-gray-900 mb-2">Business Address</h5>
                          <p className="whitespace-pre-line">{formData.businessAddress}</p>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <h5 className="font-semibold text-gray-900">INVOICE #12345</h5>
                        <p className="text-sm text-gray-600">Date: {new Date().toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Amount Due</div>
                        <div className="text-2xl font-bold text-gray-900">$149.99</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </>
        );

      case 'membership':
        return (
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            {renderSubscriptionDetails()}
          </div>
        );

      case 'payments':
        return (
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="space-y-6">
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-2xl font-bold text-white">Stripe Connect</h3>
                  <div className="flex items-center space-x-2 px-3 py-1 rounded-full border border-gray-600">
                    <div className={`w-4 h-4 rounded-full ${stripeStatus.isOnboarded ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className={`text-sm font-medium ${stripeStatus.isOnboarded ? 'text-green-400' : 'text-red-400'}`}>
                      {stripeStatus.isOnboarded ? 'Stripe connected' : 'Stripe not connected'}
                    </span>
                  </div>
                </div>
                
                {stripeStatus.isOnboarded ? (
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4 bg-gradient-to-r from-green-900/30 to-green-800/30 p-4 rounded-lg border border-green-700">
                      <div className="flex-shrink-0 mt-1">
                        <CheckCircle className="h-6 w-6 text-green-500" />
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-green-400 mb-1">Successfully Connected to Stripe</h4>
                        <p className="text-gray-300 mb-4">Your account is ready to accept payments from customers.</p>
                        <div className="flex flex-wrap gap-3">
                          <button
                            onClick={handleViewStripeAccount}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
                          >
                            <CreditCardIcon className="h-5 w-5 mr-2" />
                            View Stripe Dashboard
                          </button>
                          {/* Remove the Complete Setup button since account is already onboarded */}
                          {/* For testing purposes only */}
                          <button
                            onClick={handleDisconnectStripe}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                          >
                            Disconnect (Testing)
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4 bg-gradient-to-r from-red-900/30 to-red-800/30 p-4 rounded-lg border border-red-700">
                      <div className="flex-shrink-0 mt-1">
                        <AlertCircle className="h-6 w-6 text-red-500" />
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-red-400 mb-1">Not Connected to Stripe</h4>
                        <p className="text-gray-300">Connect your Stripe account to start accepting payments from your customers.</p>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 p-4 rounded-lg">
                      <h4 className="text-lg font-medium text-white mb-2">Benefits of Stripe Connect</h4>
                      <ul className="list-disc list-inside space-y-2 text-gray-300">
                        <li>Accept credit card payments securely</li>
                        <li>Automatic payouts to your bank account</li>
                        <li>Real-time payment tracking</li>
                        <li>Professional invoicing</li>
                      </ul>
                    </div>
                    <button
                      onClick={handleConnectStripe}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2"
                    >
                      <CreditCardIcon className="h-5 w-5" />
                      <span>Connect with Stripe</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'usage':
        return (
          <>
            <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <ChartBar className="h-5 w-5 text-blue-400 mr-2" />
                  <h3 className="text-xl font-bold text-white">Current Usage Statistics</h3>
                </div>
                <button 
                  onClick={fetchUsageStats} 
                  className="text-blue-400 hover:text-blue-300 flex items-center"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Refresh
                </button>
              </div>

              {usageLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-6 h-6 text-blue-500 animate-spin" />
                </div>
              ) : usageError ? (
                <div className="bg-red-900/30 border border-red-500 text-red-200 p-4 rounded-md mb-4">
                  <p className="flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    {usageError}
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    {/* Route Optimization Usage Card */}
                    <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-300 flex items-center">
                          <MapPin className="h-4 w-4 text-yellow-400 mr-2" />
                          Route Optimizations
                        </h4>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          usageData?.routeOptimization?.percentage >= 80 ? 'bg-red-500/20 text-red-300' : 
                          usageData?.routeOptimization?.percentage >= 50 ? 'bg-yellow-500/20 text-yellow-300' : 
                          'bg-green-500/20 text-green-300'
                        }`}>
                          {usageData?.routeOptimization?.percentage || 0}%
                        </span>
                      </div>
                      <div className="flex items-end justify-between">
                        <div className="text-2xl font-bold text-white">
                          {usageData?.routeOptimization?.used || 0} 
                          <span className="text-sm text-gray-400 ml-1">/{usageData?.routeOptimization?.limit || 10}</span>
                        </div>
                        <div className="text-sm text-gray-400">
                          {usageData?.routeOptimization?.remaining || 10} remaining
                        </div>
                      </div>
                      <div className="mt-2 w-full bg-gray-600 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            usageData?.routeOptimization?.percentage >= 80 ? 'bg-red-500' : 
                            usageData?.routeOptimization?.percentage >= 50 ? 'bg-yellow-500' : 
                            'bg-green-500'
                          }`} 
                          style={{ width: `${usageData?.routeOptimization?.percentage || 0}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Customers Usage Card */}
                    <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-300 flex items-center">
                          <Users className="h-4 w-4 text-blue-400 mr-2" />
                          Customers
                        </h4>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          usageData?.currentPeriod?.usage?.customers?.percentage >= 80 ? 'bg-red-500/20 text-red-300' : 
                          usageData?.currentPeriod?.usage?.customers?.percentage >= 50 ? 'bg-yellow-500/20 text-yellow-300' : 
                          'bg-green-500/20 text-green-300'
                        }`}>
                          {usageData?.currentPeriod?.usage?.customers?.percentage || 0}%
                        </span>
                      </div>
                      <div className="flex items-end justify-between">
                        <div className="text-2xl font-bold text-white">
                          {usageData?.currentPeriod?.usage?.customers?.used || 0} 
                          <span className="text-sm text-gray-400 ml-1">/{usageData?.currentPeriod?.usage?.customers?.limit || 25}</span>
                        </div>
                        <div className="text-sm text-gray-400">
                          {usageData?.currentPeriod?.usage?.customers?.limit - (usageData?.currentPeriod?.usage?.customers?.used || 0)} remaining
                        </div>
                      </div>
                      <div className="mt-2 w-full bg-gray-600 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            usageData?.currentPeriod?.usage?.customers?.percentage >= 80 ? 'bg-red-500' : 
                            usageData?.currentPeriod?.usage?.customers?.percentage >= 50 ? 'bg-yellow-500' : 
                            'bg-green-500'
                          }`}
                          style={{ width: `${usageData?.currentPeriod?.usage?.customers?.percentage || 0}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* SMS Usage Card */}
                    <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-300 flex items-center">
                          <MessageCircle className="h-4 w-4 text-green-400 mr-2" />
                          SMS Messages
                        </h4>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          usageData?.currentPeriod?.usage?.sms?.percentage >= 80 ? 'bg-red-500/20 text-red-300' : 
                          usageData?.currentPeriod?.usage?.sms?.percentage >= 50 ? 'bg-yellow-500/20 text-yellow-300' : 
                          'bg-green-500/20 text-green-300'
                        }`}>
                          {usageData?.currentPeriod?.usage?.sms?.percentage || 0}%
                        </span>
                      </div>
                      <div className="flex items-end justify-between">
                        <div className="text-2xl font-bold text-white">
                          {usageData?.currentPeriod?.usage?.sms?.used || 0} 
                          <span className="text-sm text-gray-400 ml-1">/{usageData?.currentPeriod?.usage?.sms?.limit || 150}</span>
                        </div>
                        <div className="text-sm text-gray-400">
                          {usageData?.currentPeriod?.usage?.sms?.limit - (usageData?.currentPeriod?.usage?.sms?.used || 0)} remaining
                        </div>
                      </div>
                      <div className="mt-2 w-full bg-gray-600 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            usageData?.currentPeriod?.usage?.sms?.percentage >= 80 ? 'bg-red-500' : 
                            usageData?.currentPeriod?.usage?.sms?.percentage >= 50 ? 'bg-yellow-500' : 
                            'bg-green-500'
                          }`} 
                          style={{ width: `${usageData?.currentPeriod?.usage?.sms?.percentage || 0}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Email Usage Card */}
                    <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-300 flex items-center">
                          <Mail className="h-4 w-4 text-blue-400 mr-2" />
                          Emails
                        </h4>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          usageData?.currentPeriod?.usage?.email?.percentage >= 80 ? 'bg-red-500/20 text-red-300' : 
                          usageData?.currentPeriod?.usage?.email?.percentage >= 50 ? 'bg-yellow-500/20 text-yellow-300' : 
                          'bg-green-500/20 text-green-300'
                        }`}>
                          {usageData?.currentPeriod?.usage?.email?.percentage || 0}%
                        </span>
                      </div>
                      <div className="flex items-end justify-between">
                        <div className="text-2xl font-bold text-white">
                          {usageData?.currentPeriod?.usage?.email?.used || 0} 
                          <span className="text-sm text-gray-400 ml-1">/{usageData?.currentPeriod?.usage?.email?.limit || 150}</span>
                        </div>
                        <div className="text-sm text-gray-400">
                          {usageData?.currentPeriod?.usage?.email?.limit - (usageData?.currentPeriod?.usage?.email?.used || 0)} remaining
                        </div>
                      </div>
                      <div className="mt-2 w-full bg-gray-600 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            usageData?.currentPeriod?.usage?.email?.percentage >= 80 ? 'bg-red-500' : 
                            usageData?.currentPeriod?.usage?.email?.percentage >= 50 ? 'bg-yellow-500' : 
                            'bg-green-500'
                          }`} 
                          style={{ width: `${usageData?.currentPeriod?.usage?.email?.percentage || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        );

      default:
        return null;
    }
  };

  // Add this function to check the Cloud Run connection
  const testCloudRunConnection = async () => {
    try {
      console.log('Testing Cloud Run connectivity...');
      console.log(`Testing connection to: ${CLOUDRUN_URL}`);
      
      // Make a simple OPTIONS request to check connectivity
      const response = await axios({
        method: 'OPTIONS',
        url: CLOUDRUN_URL,
      });
      
      console.log('Cloud Run connectivity test result:', {
        status: response.status,
        headers: response.headers,
      });
      
      return {
        success: true,
        status: response.status,
      };
    } catch (error) {
      console.error('Cloud Run connectivity test failed:', error);
      
      let errorDetails = {
        message: error.message,
      };
      
      if (error.response) {
        errorDetails.status = error.response.status;
        errorDetails.data = error.response.data;
        errorDetails.headers = error.response.headers;
      }
      
      console.error('Detailed error:', errorDetails);
      
      return {
        success: false,
        error: errorDetails,
      };
    }
  };

  // Add this to the useEffect to test connectivity on load
  useEffect(() => {
    if (currentTab === 'membership') {
      // Test Cloud Run connectivity when membership tab is active
      testCloudRunConnection().then(result => {
        console.log('Cloud Run connection test completed:', result);
      });
    }
  }, [currentTab]);

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Add the modals */}
      <ConfirmationModal 
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmDetails.onConfirm}
        title={confirmDetails.title}
        message={confirmDetails.message}
        confirmText={confirmDetails.confirmText}
        cancelText={confirmDetails.cancelText}
        isUpgrade={confirmDetails.isUpgrade}
        warningInfo={confirmDetails.warningInfo}
      />
      
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successDetails.message}
        onContinue={successDetails.onContinue}
      />
      
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Profile Settings</h1>
          <p className="mt-2 text-gray-400">Manage your account and business information</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-700">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    currentTab === tab.key
                      ? 'border-blue-500 text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {renderContent()}
      </main>
    </div>
  );
};

export default Profile;